// ============================================================================
// CrewAhead Maritime Platform - Taxonomy Registry Adapter
// ============================================================================
// Handles caching of taxonomy data.
// Reduces backend load by coalescing multiple taxonomy lookups into a single
// cached registry request.
//
// Synced with backend seamaster API registry (views.py::_build_registry).
// Backend keys are normalized by the shared Axios case interceptor.
//
// Registry endpoint: GET /api/v1/seamaster/registry
// Backend viewsets: sectors, departments, vessel-categories, vessel-types,
//   ranks, rank-vessel-types, shore-categories, shore-positions,
//   certification-categories, certifications, rank-certification-maps,
//   position-certification-maps, skills, courses, experience-levels,
//   countries, languages, document-categories, document-types, flag-states,
//   contract-types, contract-durations, employment-types, english-levels,
//   rotation-schedules, currencies
// ============================================================================

import { apiClient } from '../api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * A single taxonomy item from the backend registry.
 * All taxonomy collections share this shape.
 */
export interface TaxonomyItem {
  /** Database primary key (integer ID from backend) */
  id: number;
  /** Unique machine-readable code (e.g., 'chief_officer') */
  code: string;
  /** Human-readable label / display name (e.g., 'Chief Officer') */
  label: string;
  /** Alternative display name (some backends send 'name' instead of 'label') */
  name?: string;
  /** Optional short description */
  description?: string;
  /** Optional sort order / display priority */
  sortOrder?: number;
  /** Optional parent code for hierarchical taxonomies */
  parentCode?: string | null;
  /** Whether this item is currently active/selectable */
  isActive?: boolean;
  /** Optional alternate search terms from the taxonomy registry */
  aliases?: string[];

  // ── Contract/duration extensions ──
  /** Duration in months (ContractDuration); null for permanent */
  months?: number | null;
  /** Minimum years for experience level (ExperienceLevel) */
  yearsMin?: number | null;
  /** Maximum years for experience level (ExperienceLevel) */
  yearsMax?: number | null;

  // ── Currency extensions ──
  /** ISO 4217 currency code (Currency) */
  currencyCode?: string;
  /** Currency symbol e.g. "$", "€" (Currency) */
  symbol?: string | null;
  /** True for common maritime-hiring currencies (Currency) */
  isCommon?: boolean;

  // ── Relational foreign keys returned by backend registry ──
  /** Certification category name (Certification) */
  categoryName?: string;
  /** Vessel category parent code (VesselCategory) */
  parentCategoryCode?: string | null;
  /** Vessel category parent ID (VesselCategory) — numeric FK from registry */
  parentCategory?: number | null;
  /** Onboard rank level code/label (Rank) */
  level?: string;
  levelDisplay?: string;
  /** Onboard rank department name (Rank) */
  departmentName?: string;
  /** Department ID (Rank / ShorePosition) */
  department?: number | null;
  /** Shore category ID (ShorePosition) */
  shoreCategory?: number | null;
  /** Vessel category ID (VesselType / VesselSizeBand) */
  vesselCategory?: number | null;
  /** Certification category ID (Certification) or document category ID (DocumentType) */
  category?: number | null;
  /** Country ID (City) */
  country?: number | null;
  /** State/province ID (City) */
  state?: number | null;
  /** State/province label (City) */
  stateName?: string | null;
  /** Department type filter: "onboard" | "shore" (Department) */
  type?: string;

  /** CoC grade scope (CocGrade): FG, NCV, TUG, RATING */
  scope?: string;

  // ── Skill / Course grouping fields (backend sends skill_category / course_category) ──
  /** Skill category code (Skill) — e.g. 'navigation', 'commercial' */
  skillCategory?: string;
  /** Course category code (Course) — e.g. 'technical', 'commercial' */
  courseCategory?: string;
  /** Course type code (Course) — e.g. 'MANDATORY', 'CAREER_ADVANCEMENT' */
  courseType?: string;
  /** Human-readable category label for skills / courses */
  categoryDisplay?: string;
  /** Human-readable course type label */
  typeDisplay?: string;

  // ── VesselCategory classification flags ──
  /** True when the vessel category is a tanker (oil/chemical/product) */
  isTanker?: boolean;
  /** True when the vessel category is a gas carrier (LNG/LPG) */
  isGasCarrier?: boolean;
  /** True for bulk carrier categories */
  isBulk?: boolean;
  /** True for container vessel categories */
  isContainer?: boolean;
  /** True for passenger/cruise/ferry categories */
  isPassenger?: boolean;
  /** Rank tanker vetting minimum months in current rank */
  tankerMinMonthsInRank?: number | null;
  /** Rank tanker vetting minimum months with current operator */
  tankerMinMonthsWithOperator?: number | null;
  /** Rank tanker vetting minimum months on selected vessel category */
  tankerMinMonthsOnVesselCategory?: number | null;
  /** Rank requires cargo watchkeeper verification */
  isCargoWatchkeeper?: boolean;

  // ── ShorePosition flags (serialized by ShorePositionSerializer) ──
  /** True for technical management positions (Fleet Manager, Superintendent, etc.) */
  isTechnical?: boolean;
  /** True for marine operations / deck management positions */
  isMarine?: boolean;
  /** True for general operations / logistics positions */
  isOperations?: boolean;
  /** Alternative display titles / search keywords (ShorePosition) */
  alternativeTitles?: string;

  // ── AvailabilityStatus scope flags ──
  /** True when this status applies to seafarer profiles */
  appliesToSeafarer?: boolean;
  /** True when this status applies to shore profiles */
  appliesToShore?: boolean;

  // ── CompanyType flags ──
  /** True if company type requires an RPSL / crew-manning licence number (e.g. Manning Agent) */
  requiresRpsl?: boolean;

  // ── Country flags ──
  /** Nationality adjective (e.g. 'Indian', 'Filipino') */
  nationality?: string | null;
  /** True for major maritime crew-source countries (India, Philippines, Ukraine, etc.) */
  isCommonCrewSource?: boolean;

  // ── Generic metadata bag ──
  /** Optional metadata bag for extra fields */
  metadata?: Record<string, unknown>;

  // ── Smart taxonomy curation mapping fields (SMART-TAX-2) ──
  // All store natural-key CODE STRINGS (rank_id / position_id / category_code /
  // skill_code), never numeric IDs, and have NO DB-level referential
  // integrity — treat unresolvable codes as silent non-matches, never throw.

  /** Vessel category codes this item applies to (Certification, Rank).
   *  Empty array = universal / no vessel-category restriction. */
  applicableVesselCategories?: string[];

  /** Rank codes this item applies to (Skill.applicableRanks, Course.applicableRankCodes) */
  applicableRanks?: string[];
  applicableRankCodes?: string[];

  /** Shore position codes this item applies to (Skill, Course) */
  applicableShorePositions?: string[];
  applicableShorePositionCodes?: string[];

  /** Skill codes related to this course (Course.relatedSkillCodes) */
  relatedSkillCodes?: string[];
}

/**
 * Complete taxonomy cache returned by the registry endpoint.
 * All keys are optional because the backend may not return every category
 * (subset filtering via ?for= query param) and the registry payload only
 * includes a subset of all registered viewsets.
 *
 * Backend registry includes:
 *   sectors, departments, vessel_categories, vessel_types, ranks,
 *   shore_categories, shore_positions, certification_categories,
 *   certifications, rank_certification_maps, countries, languages,
 *   skills, courses, experience_levels, document_categories, document_types,
 *   flag_states, contract_types, contract_durations, employment_types,
 *   english_levels, rotation_schedules, currencies, availability_statuses,
 *   company_types
 *
 * Additional backend viewsets (available via individual endpoints, not
 * currently merged into the registry payload):
 *   rank_vessel_types, position_certification_maps
 */
export interface TaxonomyCache {
  // ── Industry & organisational structure ──
  /** Industry sectors (e.g., Shipping, Offshore, Cruise) */
  sectors?: TaxonomyItem[];
  /** Departments (Deck, Engine, Hospitality, etc.) */
  departments?: TaxonomyItem[];

  // ── Vessel taxonomy ──
  /** Vessel categories (Merchant, Offshore, Passenger, etc.) */
  vesselCategories?: TaxonomyItem[];
  /** Vessel types (Container Ship, Bulk Carrier, etc.) */
  vesselTypes?: TaxonomyItem[];
  /** Vessel size bands (coastal, near-coastal, unlimited, etc.) */
  vesselSizeBands?: TaxonomyItem[];
  /** Marine engine types (diesel, dual-fuel, electric, etc.) */
  engineTypes?: TaxonomyItem[];
  /** Rank-to-vessel-type mappings (available via individual endpoint) */
  rankVesselTypes?: TaxonomyItem[];

  // ── Rank & position taxonomy ──
  /** Seafarer ranks (Master, Chief Officer, etc.) */
  ranks?: TaxonomyItem[];
  /** Certificate of Competency grades / voyage scopes (FG, NCV, TUG, RATING) */
  cocGrades?: TaxonomyItem[];
  /** Shore-based position categories */
  shoreCategories?: TaxonomyItem[];
  /** Shore-based positions (Fleet Manager, Superintendent, etc.) */
  shorePositions?: TaxonomyItem[];

  // ── Certification taxonomy ──
  /** Certification categories (Deck, Engine, STCW, etc.) */
  certificationCategories?: TaxonomyItem[];
  /** Individual STCW & flag certifications */
  certifications?: TaxonomyItem[];
  /** Rank-to-certification requirement mappings */
  rankCertificationMaps?: TaxonomyItem[];
  /** Position-to-certification requirement mappings (shore) */
  positionCertificationMaps?: TaxonomyItem[];

  // ── Skills, training & experience ──
  /** Professional skills & competencies */
  skills?: TaxonomyItem[];
  /** Training courses */
  courses?: TaxonomyItem[];
  /** Skill-to-course mappings for institute offerings */
  skillCourseMaps?: TaxonomyItem[];
  /** Experience levels (Junior, Mid, Senior, etc.) */
  experienceLevels?: TaxonomyItem[];
  /** Academic degree fields (Nautical Science, Marine Engineering, etc.) */
  degreeFields?: TaxonomyItem[];
  /** Role-specific qualification rules for ranks and shore positions */
  roleQualificationRules?: TaxonomyItem[];
  /** Tanker senior officer pairing and vetting rules */
  tankerSeniorPairRules?: TaxonomyItem[];
  /** Port engagement gates for nationality/document/port constraints */
  portEngagementGates?: TaxonomyItem[];

  // ── Geography & flag states ──
  /** Countries for nationality, residence, issuance */
  countries?: TaxonomyItem[];
  /** State/province rows for country-scoped city narrowing */
  states?: TaxonomyItem[];
  /** Cities / ports within countries for residence and work-location pickers */
  cities?: TaxonomyItem[];
  /** Flag states with issuance capabilities & white-list status */
  flagStates?: TaxonomyItem[];
  /** Trading areas (Asia, Europe, Middle East, North America, etc.) */
  tradingAreas?: TaxonomyItem[];

  // ── Documents ──
  /** Document categories (Identity, Medical, Certificate, Visa, etc.) */
  documentCategories?: TaxonomyItem[];
  /** Document types (Passport, Seaman's Book, COC, US Visa, etc.) */
  documentTypes?: TaxonomyItem[];

  // ── Contract, employment & compensation ──
  /** Contract types (Permanent, Fixed-Term, Voyage, Rotational, etc.) */
  contractTypes?: TaxonomyItem[];
  /** Contract durations (3/4/6/9/12 months, Permanent) */
  contractDurations?: TaxonomyItem[];
  /** Employment types (Full-time, Part-time, Contract, Consultant) */
  employmentTypes?: TaxonomyItem[];
  /** English proficiency levels (Basic, Conversational, Good, Fluent, Native) */
  englishLevels?: TaxonomyItem[];
  /** Onboard rotation patterns (2/1, 3/1, 4/2, etc.) */
  rotationSchedules?: TaxonomyItem[];
  /** Currencies (ISO 4217 codes; `is_common` flags maritime-hiring currencies) */
  currencies?: TaxonomyItem[];
  /** Languages (ISO 639 codes; `is_common` flags common crew languages) */
  languages?: TaxonomyItem[];
  /** Availability statuses (Available, Not Available, Employed Open, etc.) — shared seafarer/shore vocabulary */
  availabilityStatuses?: TaxonomyItem[];
  /** Availability post types (onboard, shore, both) */
  availabilityPostTypes?: TaxonomyItem[];
  /** Company types (Ship Owner, Manning Agency, etc.) */
  companyTypes?: TaxonomyItem[];

  // ── Team / account roles ──
  /** Employer team roles (ADMIN, CREWING_MANAGER, etc.) */
  teamRoles?: TaxonomyItem[];

  /** Job post statuses (draft, published, closed, filled, expired) */
  jobStatuses?: TaxonomyItem[];

  // ── Client-side metadata ──
  /** Cache timestamp (added client-side, not from backend) */
  timestamp: number;
}

  /** Valid taxonomy category keys (excludes client-side metadata). */
export type TaxonomyCategory = keyof Omit<TaxonomyCache, 'timestamp'>;

// ─── Adapter ─────────────────────────────────────────────────────────────────

/**
 * Singleton adapter that fetches the full taxonomy registry once and
 * caches it for subsequent lookups.
 *
 * @example
 * ```ts
 * // Get the entire registry (cached)
 * const taxonomy = await taxonomyAdapter.getTaxonomy();
 * const ranks = taxonomy.ranks;
 *
 * // Look up a single item
 * const chiefOfficer = await taxonomyAdapter.getItem('ranks', 'chief_officer');
 *
 * // Get all items in a category
 * const allVessels = await taxonomyAdapter.getItems('vesselTypes');
 * ```
 */
class TaxonomyAdapter {
  private cache: TaxonomyCache | null = null;
  private fetchPromise: Promise<TaxonomyCache> | null = null;
  private readonly endpoint: string;

  constructor(endpoint: string = '/seamaster/registry') {
    this.endpoint = endpoint;
  }

  /**
   * Fetch taxonomy registry once and cache it.
   * Subsequent calls return cached data.
   * Prevents cache stampede via request coalescing.
   * On failure logs the error and returns an empty cache so consumers never
   * see an infinite loading state.
   */
  async getTaxonomy(): Promise<TaxonomyCache> {
    // Coalesce concurrent requests (cache stampede mitigation)
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = this.fetchAndCache().catch((error) => {
      console.error('[TaxonomyAdapter] Registry fetch failed:', error);
      return { timestamp: Date.now() } as TaxonomyCache;
    });

    try {
      const result = await this.fetchPromise;
      return result;
    } finally {
      this.fetchPromise = null;
    }
  }

  private async fetchAndCache(): Promise<TaxonomyCache> {
    const response = await apiClient.get<Omit<TaxonomyCache, 'timestamp'>>(
      this.endpoint
    );

    // TEMPORARY: log raw registry response for contract verification
    console.info('[TaxonomyAdapter] Raw registry response:', response.data);

    // Cache with client-side timestamp
    this.cache = {
      ...response.data,
      timestamp: Date.now(),
    };

    return this.cache;
  }

  /**
   * Look up a single taxonomy item by its category and code.
   *
   * @param type - Taxonomy category (e.g., 'ranks')
   * @param code - Item code (e.g., 'chief_officer')
   * @returns The matching item or null if not found
   *
   * @example
   * ```ts
   * const rank = await taxonomyAdapter.getItem('ranks', 'chief_officer');
   * // → { code: 'chief_officer', label: 'Chief Officer', ... }
   * ```
   */
  async getItem(type: TaxonomyCategory, code: string): Promise<TaxonomyItem | null> {
    const taxonomy = await this.getTaxonomy();
    const items = taxonomy[type];
    if (!Array.isArray(items)) return null;
    return items.find((item) => item.code === code) ?? null;
  }

  /**
   * Get all items in a taxonomy category.
   *
   * @param type - Taxonomy category (e.g., 'vesselTypes')
   * @returns Array of taxonomy items (empty if category missing)
   *
   * @example
   * ```ts
   * const vessels = await taxonomyAdapter.getItems('vesselTypes');
   * // → [{ code: 'container_ship', label: 'Container Ship' }, ...]
   * ```
   */
  async getItems(type: TaxonomyCategory): Promise<TaxonomyItem[]> {
    const taxonomy = await this.getTaxonomy();
    const items = taxonomy[type];
    return Array.isArray(items) ? items : [];
  }

  /**
   * Get a label for a given code, with a fallback.
   * Convenience method for display purposes.
   *
   * @param type - Taxonomy category
   * @param code - Item code
   * @param fallback - Fallback label if not found (defaults to code itself)
   * @returns Human-readable label
   *
   * @example
   * ```ts
   * const label = await taxonomyAdapter.getLabel('ranks', 'chief_officer', 'Unknown');
   * // → 'Chief Officer'
   * ```
   */
  async getLabel(
    type: TaxonomyCategory,
    code: string,
    fallback: string = code
  ): Promise<string> {
    const item = await this.getItem(type, code);
    return item?.label ?? fallback;
  }

  /**
   * Clear the in-memory cache.
   * Useful after knowing taxonomy data has changed, or for testing.
   */
  clearCache(): void {
    this.cache = null;
    this.fetchPromise = null;
  }

  /**
   * Check if the cache is currently populated.
   */
  isCached(): boolean {
    return this.cache !== null;
  }
}

// ─── Singleton Instance ──────────────────────────────────────────────────────

/**
 * Shared taxonomy adapter instance.
 * Single cache per application lifetime.
 */
export const taxonomyAdapter = new TaxonomyAdapter();

/** Fetch the full taxonomy registry (cached). */
export const getTaxonomy = (): Promise<TaxonomyCache> => taxonomyAdapter.getTaxonomy();

// NOTE: Item/category/label lookups are exposed through @/lib/taxonomy-service
// so that all taxonomy access shares the same safe fallback behavior.
// Import getTaxonomyItem / getTaxonomyItems / getTaxonomyLabel from there.
